import React, { useMemo, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useOptimizationRuns,
  type OptimizationRunSummary,
} from '@/hooks/optimization/useOptimizationRuns';
import { RunDetailDrawer } from './RunDetailDrawer';

const STATUS_LABEL: Record<string, string> = {
  pending: 'pending',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
};

interface RunsHistoryTabProps {
  locationId: string | null;
}

function formatKzt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
}

function statusBadge(s: string) {
  const map: Record<string, string> = {
    completed: 'bg-green-100 text-green-800 border-green-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
    running: 'bg-blue-100 text-blue-800 border-blue-300',
    pending: 'bg-gray-100 text-gray-800 border-gray-300',
  };
  return (
    <Badge variant="outline" className={`${map[s] ?? ''} font-mono text-[10px]`}>
      {STATUS_LABEL[s] ?? s}
    </Badge>
  );
}

export const RunsHistoryTab: React.FC<RunsHistoryTabProps> = ({ locationId }) => {
  const { data: runs, isLoading } = useOptimizationRuns(locationId ?? undefined);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!runs) return [];
    return statusFilter === 'all'
      ? runs
      : runs.filter((r) => r.status === statusFilter);
  }, [runs, statusFilter]);

  if (!locationId) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Выберите локацию.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-purple-600" />
          <div>
            <div className="font-medium">История запусков</div>
            <div className="text-xs text-muted-foreground">
              Последние 50 запусков оптимизации для этой локации.
            </div>
          </div>
        </div>
        <div className="w-44">
          <Label className="text-xs">Статус</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все</SelectItem>
              <SelectItem value="completed">completed</SelectItem>
              <SelectItem value="running">running</SelectItem>
              <SelectItem value="failed">failed</SelectItem>
              <SelectItem value="pending">pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-md">
          Нет запусков. Запустите оптимизацию в «Управление графиками» →
          «Сгенерировать AI».
        </div>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Создан</TableHead>
                <TableHead>Период</TableHead>
                <TableHead>Сценарий</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">ФОТ</TableHead>
                <TableHead className="text-right">Покрытие</TableHead>
                <TableHead className="text-right">Недокрытие</TableHead>
                <TableHead></TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <Row
                  key={r.id}
                  run={r}
                  onOpen={() => setSelectedRunId(r.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {selectedRunId && (
        <RunDetailDrawer
          runId={selectedRunId}
          open={!!selectedRunId}
          onClose={() => setSelectedRunId(null)}
          onCompare={(id) => {
            // Compare-режим: открываем drawer того же run с outer state.
            // Drawer внутри сам управляет CompareRunsDialog по компании рядом.
            setSelectedRunId(id);
          }}
        />
      )}
    </div>
  );
};

const Row: React.FC<{
  run: OptimizationRunSummary;
  onOpen: () => void;
}> = ({ run, onOpen }) => {
  const created = new Date(run.created_at);
  const createdStr = created.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <TableRow className="hover:bg-gray-50 cursor-pointer" onClick={onOpen}>
      <TableCell className="font-mono text-xs">{createdStr}</TableCell>
      <TableCell className="text-xs">
        {run.period_start} → {run.period_end}
      </TableCell>
      <TableCell className="text-xs">{run.scenario_code}</TableCell>
      <TableCell>{statusBadge(run.status)}</TableCell>
      <TableCell className="text-right text-xs">
        {run.total_fot !== null ? `${formatKzt(run.total_fot)} ₸` : '—'}
      </TableCell>
      <TableCell className="text-right text-xs">
        {run.coverage_score !== null
          ? `${(run.coverage_score * 100).toFixed(0)}%`
          : '—'}
      </TableCell>
      <TableCell className="text-right text-xs">
        {run.understaffed_hours !== null ? `${run.understaffed_hours} ч` : '—'}
      </TableCell>
      <TableCell>
        {run.applied_as_request_id && (
          <Badge
            variant="outline"
            className="text-[10px] bg-blue-50 border-blue-200 text-blue-700"
          >
            применён
          </Badge>
        )}
        {run.parent_run_id && (
          <Badge
            variant="outline"
            className="ml-1 text-[10px] bg-purple-50 border-purple-200 text-purple-700"
          >
            what-if
          </Badge>
        )}
        {run.position_ids && run.position_ids.length > 0 && (
          <Badge
            variant="outline"
            className="ml-1 text-[10px] bg-amber-50 border-amber-200 text-amber-800"
            title={`Per-position run: ${run.position_ids.length} должностей`}
          >
            {run.position_ids.length === 1
              ? '1 должность'
              : `${run.position_ids.length} должностей`}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          Открыть
        </Button>
      </TableCell>
    </TableRow>
  );
};
